import React, { createContext, useContext, useEffect } from 'react';
import { startOfDay } from 'date-fns';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { stringify } from 'query-string';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import { pageSummaryKey } from '@dashboard/lib/queryKeys';
import useStatisticsRange from '@dashboard/hooks/useStatisticsRange';
import StatisticsRangeControls from '@dashboard/components/StatisticsRangeControls';

const PageSummaryContext = createContext(null);

/** The Summary's date range and the figures the API returned for it. */
export const usePageSummary = () => useContext(PageSummaryContext);

export function PageSummaryRangeProvider({ page, children }) {
  const { token, sellerId } = useAuth();
  const statisticsRange = useStatisticsRange({
    storageKey: 'page-summary',
    defaultRange: 'allTime',
    bareDefaultUrl: true,
    allTimeStart: page.createdAt ? startOfDay(new Date(page.createdAt)) : null,
  });
  const { dateStart, dateEnd, unit, setUnit, timezoneOffset, range } = statisticsRange;
  const isAllTime = range === 'allTime';

  const query = {
    // All time starts at the page's first record, which only the API knows.
    ...(isAllTime ? {} : { dateStart: dateStart.toISOString() }),
    dateEnd: dateEnd.toISOString(),
    unit,
    timezoneOffset,
  };
  const { data, isPending, isPlaceholderData } = useQuery({
    queryKey: pageSummaryKey(page._id, query),
    queryFn: () =>
      fetcher(`/sellers/${sellerId}/pages/${page._id}/summary?${stringify(query)}`, token),
    enabled: !!sellerId && !!page._id,
    placeholderData: keepPreviousData,
  });

  // The API steps a too-fine unit up (days across years), so the unit select follows it.
  const servedUnit = isPlaceholderData ? null : data?.unit;
  useEffect(() => {
    if (servedUnit && servedUnit !== unit) setUnit(servedUnit);
  }, [servedUnit, unit, setUnit]);

  return (
    <PageSummaryContext.Provider
      value={{
        statisticsRange,
        isAllTime,
        summary: data,
        isLoading: isPending,
      }}
    >
      {children}
    </PageSummaryContext.Provider>
  );
}

export function PageSummaryRangeControls() {
  const { statisticsRange } = usePageSummary();
  return <StatisticsRangeControls statisticsRange={statisticsRange} />;
}
